import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Users, TrendingUp, UserCheck } from 'lucide-react';

const About = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const features = [
    {
      icon: Users,
      title: t.about.feature1Title,
      description: t.about.feature1Desc
    },
    {
      icon: TrendingUp,
      title: t.about.feature2Title,
      description: t.about.feature2Desc
    },
    {
      icon: UserCheck,
      title: t.about.feature3Title,
      description: t.about.feature3Desc
    }
  ];

  return (
    <section
      id="about"
      ref={sectionRef}
      className="min-h-screen py-20 md:py-32 px-6 bg-gray-50 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 w-1/2 h-full opacity-5 bg-gray-900" />
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className={`text-center mb-12 md:mb-16 transition-all duration-1000 transform ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2
            className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6 text-black"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            {t.about.title}
          </h2>
          <p className="text-base md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed whitespace-pre-line">
            {t.about.description}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-0">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className={`group p-6 md:p-8 bg-white rounded-xl transition-all duration-700 transform text-center min-h-[280px] md:min-h-[320px] flex flex-col justify-center ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                } hover:-translate-y-2 hover:shadow-2xl border border-gray-100`}
                style={{
                  transitionDelay: `${index * 150}ms`
                }}
              >
                <div className="flex justify-center mb-3 md:mb-4">
                  <div className="p-3 md:p-4 bg-black rounded-lg group-hover:scale-110 transition-transform duration-300">
                    <Icon 
                      className="text-white" 
                      size={28}
                    />
                  </div>
                </div>
                <h3 className="text-xl md:text-2xl font-semibold mb-2 md:mb-3 text-black whitespace-pre-line" style={{ fontFamily: 'Crimson Text, serif' }}>
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-gray-700">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default About;